import { useCallback, useEffect, useRef, useState } from 'react';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';

import { db } from '../utils/firebase';
import { enrichHospitalityReviewDocs } from '../utils/enrichHospitalityReviewDocs';
import { REVIEW_DETAILS_PAGE_SIZE } from '../utils/reviewDetails/reviewDetailsConstants';

/**
 * @param {object} params
 * @param {string} [params.breweryId]
 * @param {boolean} [params.enabled]
 * @param {number} [params.pageSize]
 */
function usePaginatedHospitalityReviewsForBrewery({
  breweryId,
  enabled = false,
  pageSize = REVIEW_DETAILS_PAGE_SIZE,
}) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const lastDocRef = useRef(null);
  const fetchGenerationRef = useRef(0);

  const resetState = useCallback(() => {
    setReviews([]);
    setError(null);
    setHasMore(false);
    lastDocRef.current = null;
  }, []);

  const fetchPage = useCallback(async ({ append = false }) => {
    if (typeof breweryId !== 'string' || breweryId.length === 0) {
      resetState();
      setLoading(false);
      setLoadingMore(false);
      return;
    }

    const requestGeneration = fetchGenerationRef.current;

    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setError(null);
      }

      const reviewsRef = collection(db, 'hospitalityReview');
      const reviewsQuery = lastDocRef.current && append
        ? query(
          reviewsRef,
          where('breweryId', '==', breweryId),
          orderBy('date', 'desc'),
          startAfter(lastDocRef.current),
          limit(pageSize),
        )
        : query(
          reviewsRef,
          where('breweryId', '==', breweryId),
          orderBy('date', 'desc'),
          limit(pageSize),
        );

      const reviewsSnapshot = await getDocs(reviewsQuery);
      if (requestGeneration !== fetchGenerationRef.current) {
        return;
      }

      const pageReviews = await enrichHospitalityReviewDocs(reviewsSnapshot.docs);
      if (requestGeneration !== fetchGenerationRef.current) {
        return;
      }

      lastDocRef.current = reviewsSnapshot.docs[reviewsSnapshot.docs.length - 1] ?? null;
      setHasMore(reviewsSnapshot.docs.length === pageSize);
      setReviews((currentReviews) => (
        append ? [...currentReviews, ...pageReviews] : pageReviews
      ));
    } catch (fetchError) {
      if (requestGeneration !== fetchGenerationRef.current) {
        return;
      }
      console.error('Error fetching paginated hospitality reviews:', fetchError);
      setError(fetchError.message);
      if (!append) {
        setReviews([]);
      }
    } finally {
      if (requestGeneration === fetchGenerationRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [breweryId, pageSize, resetState]);

  useEffect(() => {
    if (!enabled) {
      resetState();
      setLoading(false);
      setLoadingMore(false);
      return;
    }
    fetchGenerationRef.current += 1;
    lastDocRef.current = null;
    fetchPage({ append: false });
  }, [breweryId, enabled, fetchPage, resetState]);

  const loadMore = useCallback(() => {
    if (!enabled || loading || loadingMore || !hasMore) {
      return;
    }
    fetchPage({ append: true });
  }, [enabled, fetchPage, hasMore, loading, loadingMore]);

  return {
    reviews,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
  };
}

export default usePaginatedHospitalityReviewsForBrewery;
